/**
 * Full Event Modal
 * @format
 */

import React, { useState, useEffect } from 'react';
import { loadVideoScript } from '@services/video-services';
import { removeTags } from '@services/string-transformation-services';
import ModalWrapper from '@components/modals/modal-wrapper';

/**
 * Event Modal Props.
 */
interface EventModalProps {
  cardImage: string;
  cardContent: string;
  cardVideo: null | string;
  linkUrl: null | string;
  linkText: null | string;
  hideLink: boolean;
  showImage: boolean;
  showModal: boolean;
  videoId: null | string;
}

/**
 * Full Event Modal.
 *
 * @returns
 */
const FullEventModal = (): JSX.Element => {
  /**
   * States.
   */
  const [showModal, setShowModal] = useState(false);
  const [modalStates, setModalStates] = useState<EventModalProps>({
    cardImage: '',
    showImage: false,
    cardContent: '',
    cardVideo: null,
    linkUrl: null,
    linkText: null,
    hideLink: false,
    showModal: false,
    videoId: null,
  });
  const [cardSetup, setCardSetup] = useState<boolean>(false);

  /**
   * Close Overlay.
   */
  const closeModal = (): void => {
    setModalStates({
      cardImage: '',
      showImage: false,
      cardContent: '',
      cardVideo: null,
      linkUrl: null,
      linkText: null,
      hideLink: false,
      showModal: false,
      videoId: null,
    });
  };

  /**
   * Get Registration Link.
   */
  const getRegistrationLink = (
    card: Element
  ): Record<string, string | null> => {
    const sessionGroup = card.closest('.session-group');
    let registrationLink: HTMLAnchorElement | null =
      document.querySelector('.registration-link');
    if (sessionGroup) {
      const groupLink: HTMLAnchorElement | null =
        sessionGroup.querySelector('.registration-link');
      registrationLink = groupLink ? groupLink : registrationLink;
    }
    return {
      linkUrl: registrationLink ? registrationLink.href : null,
      linkText: registrationLink ? registrationLink.innerText : null,
    };
  };

  /**
   * Setup Cards.
   */
  const setupCards = (eventCards: NodeListOf<Element>): void => {
    eventCards.forEach((card: Element) => {
      let videoIdCheck: string | null = null;
      const content: Element | null = card.querySelector('.full-modal-content');

      if (content) {
        videoIdCheck = content.getAttribute('data-video-id');
        if (videoIdCheck) {
          card.classList.add('contains-video');
        }
      }

      /**
       * Card Click.
       */
      card.addEventListener('click', (event): void => {
        event.preventDefault();

        if (content) {
          const registrationLink = getRegistrationLink(card);
          const eventImage: Element | null =
            card.querySelector('.img-container');

          setModalStates({
            cardImage: eventImage ? eventImage.innerHTML : '',
            showImage: content.getAttribute('data-show-modal-image') === 'true',
            cardContent: content.innerHTML,
            cardVideo: videoIdCheck,
            linkUrl: registrationLink ? registrationLink.linkUrl : null,
            linkText: registrationLink ? registrationLink.linkText : null,
            hideLink:
              content.getAttribute('data-show-registration-link') === 'false' ||
              registrationLink.linkUrl === null,
            videoId: videoIdCheck,
            showModal: true,
          });

          if (videoIdCheck) {
            setTimeout(() => {
              loadVideoScript(`video-${removeTags(videoIdCheck)}`, true);
            }, 10);
          }
        }
      });
    });
  };

  /**
   * Use Effect.
   */
  useEffect(() => {
    const eventCards: NodeListOf<Element> = document.querySelectorAll(
      '.full-modal-event-card'
    );
    if (eventCards.length > 0 && !cardSetup) {
      setupCards(eventCards);
      setCardSetup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Listens for modal closing to set modalStates.
   */
  useEffect(() => {
    if (!showModal) {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Render.
   */
  return (
    <ModalWrapper showModal={modalStates.showModal} setShowModal={setShowModal}>
      <>
        {/* Image */}
        {modalStates.cardImage &&
          modalStates.showImage &&
          !modalStates.videoId && (
            <div
              className="img-container"
              dangerouslySetInnerHTML={{ __html: modalStates.cardImage }}
            />
          )}

        {/* Video */}
        {modalStates.videoId && (
          <video-js
            className="video-js vjs-fluid"
            data-account="2418587696001"
            data-player="FoCKoQVZW"
            data-embed="default"
            controls=""
            data-video-id={modalStates.videoId}
            data-playlist-id=""
            data-application-id=""
            id={`video-${modalStates.videoId}`}
          />
        )}

        {/* Content */}
        {modalStates.cardContent && (
          <div
            className="event-modal-content"
            dangerouslySetInnerHTML={{ __html: modalStates.cardContent }}
          />
        )}

        {/* Link */}
        {modalStates.linkUrl && !modalStates.hideLink && (
          <div className="registration-link-container">
            <a
              href={modalStates.linkUrl}
              target="_blank"
              className="btn-primary"
              rel="noreferrer noopener">
              {modalStates.linkText}
            </a>
          </div>
        )}
      </>
    </ModalWrapper>
  );
};
export default FullEventModal;
